import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import type { components } from '@/api/qraft/schema';
import { CourseHeader, TabHeading } from '~/components/ui/ContentNav';
import {
  type ContentSearchParams,
  parseContentSearch,
  SearchFilters,
  searchQuery,
} from '~/components/ui/ContentSearch';
import { PageTitle } from '~/components/ui/Page';
import { LIST_PAGE_SIZE, Pagination } from '~/components/ui/Pagination';
import { Table } from '~/components/ui/Table';
import { NeedsNewVersion } from '~/components/ui/Versions';
import { LANGUAGE_LABELS } from '~/constants/languages';
import { CreateLessonButton } from './CreateLessonButton';

type LessonAdminSummary = components['schemas']['LessonAdminSummary'];
type LessonSearchItem = components['schemas']['LessonSearchItem'];

export const Route = createFileRoute('/_admin/lessons/')({
  validateSearch: (
    s: Record<string, unknown>,
  ): { course?: number; version?: number } & ContentSearchParams => ({
    // With a course, its lessons; without, a search across every course.
    course: s.course != null ? Number(s.course) : undefined,
    version: s.version != null ? Number(s.version) : undefined,
    ...parseContentSearch(s),
  }),
  component: LessonsPage,
});

function LessonsPage() {
  const { course, version } = Route.useSearch();
  return course !== undefined && version !== undefined ? (
    <CourseLessons course={course} version={version} />
  ) : (
    <LessonSearch />
  );
}

const columnHelper = createColumnHelper<LessonAdminSummary>();

const columns = [
  columnHelper.accessor('order', { header: 'Order' }),
  columnHelper.accessor('id', { header: 'ID' }),
  columnHelper.accessor('title', { header: 'Title' }),
  columnHelper.accessor('status', { header: 'Status' }),
];

function CourseLessons({
  course,
  version,
}: {
  course: number;
  version: number;
}) {
  const [page, setPage] = useState(0);

  // Admin endpoint: all statuses (the student course_lessons is gated to a
  // fully published path).
  const { data, isLoading, error } = api.adminListLessons.useQuery({
    path: { course_id: course },
    query: { version, limit: LIST_PAGE_SIZE, offset: LIST_PAGE_SIZE * page },
  });
  const courseQuery = api.adminGetCourse.useQuery({
    path: { course_id: course },
    query: { version },
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (isLoading) return <Message>Loading lessons...</Message>;
  if (error) return <Message $error>Error loading lessons</Message>;

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={setPage}
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      {courseQuery.data && <CourseHeader course={courseQuery.data} />}
      <TabHeading
        action={
          courseQuery.data?.version_state === 'draft' && (
            <CreateLessonButton courseId={course} version={version} />
          )
        }
      >
        Lessons
      </TabHeading>
      {courseQuery.data && courseQuery.data.version_state !== 'draft' && (
        <NeedsNewVersion target={{ courseId: course, version }}>
          Lessons can't be added, removed or reordered in a published version.
        </NeedsNewVersion>
      )}
      {pagination()}

      <Table
        table={table}
        to="/lessons/$lessonId"
        getParams={(row) => ({ lessonId: String(row.id) })}
      />
      {pagination(true)}
    </div>
  );
}

const searchColumnHelper = createColumnHelper<LessonSearchItem>();

const searchColumns = [
  searchColumnHelper.accessor('id', { header: 'ID' }),
  searchColumnHelper.accessor('title', { header: 'Title' }),
  searchColumnHelper.accessor('course_title', { header: 'Course' }),
  searchColumnHelper.accessor('language', {
    header: 'Language',
    cell: (info) => LANGUAGE_LABELS[info.getValue()],
  }),
  searchColumnHelper.accessor('status', { header: 'Status' }),
];

function LessonSearch() {
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const page = (params.page ?? 1) - 1;

  const { data, isLoading, error } = api.adminSearchLessons.useQuery({
    query: searchQuery(params, LIST_PAGE_SIZE),
  });

  const table = useReactTable({
    data: data?.data ?? [],
    columns: searchColumns,
    getCoreRowModel: getCoreRowModel(),
  });

  const update = (patch: ContentSearchParams, replace = true) =>
    navigate({ search: (prev) => ({ ...prev, ...patch }), replace });

  const pagination = (hideWhenAlone = false) => (
    <Pagination
      page={page}
      pageSize={LIST_PAGE_SIZE}
      shown={data?.data.length ?? 0}
      total={data?.count}
      onPage={(next) =>
        update({ page: next > 0 ? next + 1 : undefined }, false)
      }
      hideWhenAlone={hideWhenAlone}
    />
  );

  return (
    <div>
      <PageTitle>Lessons</PageTitle>
      <Intro>Every lesson in every course, drafts included.</Intro>
      <SearchFilters
        params={params}
        onChange={update}
        placeholder="Search lessons by title or ID"
      />
      {isLoading ? (
        <Message>Loading lessons...</Message>
      ) : error ? (
        <Message $error>Error loading lessons</Message>
      ) : data?.count === 0 ? (
        <Message>No lessons match.</Message>
      ) : (
        <>
          {pagination()}
          <Table
            table={table}
            to="/lessons/$lessonId"
            getParams={(row) => ({ lessonId: String(row.id) })}
          />
          {pagination(true)}
        </>
      )}
    </div>
  );
}

const Intro = styled.p`
  color: var(--grey-700);
  font-size: 0.875rem;
  margin: 0.25rem 0 0;
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
